/* =========================================================
   EVR Reveal (IntersectionObserver)
   - Shopify theme-editor safe (re-inits on section load)
   - Supports once vs re-animate
   - Supports per-element delay/duration via data attributes
   - Supports parent staggering via data-evr-stagger
   - FIX: above-the-fold elements animate on initial viewport (no scroll)

   - Mode attributes:
     data-evr-reveal-desktop  => reveal only on desktop (>= 750px)
     data-evr-reveal-mobile   => reveal only on mobile  (<= 749px)
   ========================================================= */

(function () {
  const SELECTOR = '[data-evr-reveal]';
  const STAGGER_SELECTOR = '[data-evr-stagger]';

  // Breakpoint
  const DESKTOP_MIN = 750;

  // Mode attributes
  const DESKTOP_ONLY_ATTR = 'data-evr-reveal-desktop';
  const MOBILE_ONLY_ATTR = 'data-evr-reveal-mobile';

  // Single shared observer for performance
  let observer;

  // Tracks first init cycle to ensure above-the-fold items animate
  let firstInitDone = false;

  function toBool(val, fallback) {
    if (val === undefined || val === null || val === '') return fallback;
    return String(val).toLowerCase() === 'true';
  }

  function isDesktop() {
    return window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;
  }

  function shouldDisableReveal(el) {
    const desktop = isDesktop();
    if (el.hasAttribute(DESKTOP_ONLY_ATTR) && !desktop) return true; // desktop-only, but currently mobile
    if (el.hasAttribute(MOBILE_ONLY_ATTR) && desktop) return true;    // mobile-only, but currently desktop
    return false;
  }

  function initObserver() {
    if (observer) return observer;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;

          // If mode says "disabled here", force visible and stop
          if (shouldDisableReveal(el)) {
            forceVisible(el);
            if (observer) observer.unobserve(el);
            return;
          }

          const once = toBool(el.getAttribute('data-evr-once'), true);

          if (entry.isIntersecting) {
            // Defer class toggle so the browser paints the "hidden" state first.
            scheduleReveal(el, once);
          } else {
            if (!once) {
              el.classList.remove('is-inview');
              // Allow re-animate next time it comes into view
              delete el.dataset.evrRevealQueued;
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12,
      }
    );

    return observer;
  }

  function applyTimingVars(el) {
    const delay = el.getAttribute('data-evr-delay');
    const duration = el.getAttribute('data-evr-duration');

    if (delay !== null && delay !== '') {
      el.style.setProperty('--evr-reveal-delay', `${parseInt(delay, 10)}ms`);
    }
    if (duration !== null && duration !== '') {
      el.style.setProperty('--evr-reveal-duration', `${parseInt(duration, 10)}ms`);
    }
  }

  function clearTimingVars(el) {
    el.style.removeProperty('--evr-reveal-delay');
    el.style.removeProperty('--evr-reveal-duration');
  }

  function applyStagger(root) {
    const parents = root.querySelectorAll(STAGGER_SELECTOR);
    parents.forEach((parent) => {
      const base = parseInt(parent.getAttribute('data-evr-stagger'), 10);
      const step = isNaN(base) ? 90 : base;
      const start = parseInt(parent.getAttribute('data-evr-stagger-start'), 10);
      const startDelay = isNaN(start) ? 0 : start;

      const children = parent.querySelectorAll(':scope > [data-evr-reveal]');
      children.forEach((child, idx) => {
        const explicit = child.getAttribute('data-evr-delay');
        if (explicit === null || explicit === '') {
          child.style.setProperty('--evr-reveal-delay', `${startDelay + idx * step}ms`);
        }
      });
    });
  }

  function inViewport(el) {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;

    const verticallyVisible = r.bottom > 0 && r.top < vh;
    const horizontallyVisible = r.right > 0 && r.left < vw;

    return verticallyVisible && horizontallyVisible && r.width > 0 && r.height > 0;
  }

  function markForced(el) {
    el.dataset.evrForcedVisible = '1';
  }

  function wasForced(el) {
    return el.dataset.evrForcedVisible === '1';
  }

  function clearForced(el) {
    delete el.dataset.evrForcedVisible;
  }

  function forceVisible(el) {
    el.classList.add('is-inview');
    clearTimingVars(el);
    delete el.dataset.evrRevealQueued;
    markForced(el);
  }

  function resetForRevealIfNeeded(el) {
    // If the element was previously "forced visible" due to mode mismatch,
    // and now the mode allows reveal, reset it to hidden so it can animate again.
    if (!wasForced(el)) return;

    // Don't fight reveal="none"
    if (el.getAttribute('data-evr-reveal') === 'none') {
      clearForced(el);
      return;
    }

    el.classList.remove('is-inview');
    delete el.dataset.evrRevealQueued;
    delete el.dataset.evrObserved;
    clearForced(el);
  }

  function scheduleReveal(el, once) {
    // Avoid re-scheduling for the same "enter" cycle
    if (el.dataset.evrRevealQueued === '1') return;
    el.dataset.evrRevealQueued = '1';

    // If reveal="none", force visible immediately
    if (el.getAttribute('data-evr-reveal') === 'none') {
      el.classList.add('is-inview');
      if (once && observer) observer.unobserve(el);
      return;
    }

    // Double rAF ensures:
    // 1) initial hidden state is painted
    // 2) class toggle happens after paint -> transition runs
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add('is-inview');
        if (once && observer) observer.unobserve(el);
      });
    });
  }

  function observeWithin(root, opts) {
    const obs = initObserver();
    const options = opts || {};
    const resetKickstart = !!options.resetKickstart;

    // Stagger first so delays are ready before observe
    applyStagger(root);

    const els = root.querySelectorAll(SELECTOR);

    els.forEach((el) => {
      // Mode disabling
      if (shouldDisableReveal(el)) {
        forceVisible(el);
        if (observer) observer.unobserve(el);
        // Make sure if viewport later switches, it can animate again
        delete el.dataset.evrObserved;
        return;
      }

      // Mode now allows reveal; if it was forced earlier, reset it so it can animate
      resetForRevealIfNeeded(el);

      if (el.getAttribute('data-evr-reveal') === 'none') {
        el.classList.add('is-inview');
        return;
      }

      if (el.dataset.evrObserved === '1') return;
      el.dataset.evrObserved = '1';

      applyTimingVars(el);
      obs.observe(el);
    });

    // Kickstart above-the-fold:
    // Run once initially, and optionally again on mode changes (resize).
    if (!firstInitDone || resetKickstart) {
      firstInitDone = true;

      requestAnimationFrame(() => {
        els.forEach((el) => {
          // Skip disabled mode items
          if (shouldDisableReveal(el)) return;
          if (el.classList.contains('is-inview')) return;
          if (el.getAttribute('data-evr-reveal') === 'none') return;

          if (inViewport(el)) {
            const once = toBool(el.getAttribute('data-evr-once'), true);
            scheduleReveal(el, once);
          }
        });
      });
    }
  }

  function initAll(opts) {
    observeWithin(document, opts);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Shopify Theme Editor: re-init when a section is (re)loaded
  document.addEventListener('shopify:section:load', (event) => {
    if (!event || !event.target) return;
    observeWithin(event.target, { resetKickstart: true });
  });

  document.addEventListener('shopify:section:reorder', () => initAll({ resetKickstart: true }));
  document.addEventListener('shopify:section:select', (event) => {
    if (event && event.target) observeWithin(event.target, { resetKickstart: true });
  });

  // Handle viewport mode changes (desktop <-> mobile)
  let resizeRaf = null;
  window.addEventListener(
    'resize',
    () => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        // Re-evaluate mode-only elements + kickstart in-view elements after reset
        initAll({ resetKickstart: true });
      });
    },
    { passive: true }
  );
})();